import React from "react";
import { UserCoverStyle } from "authory-api-types/dist/enums";
import { UserResponse } from "authory-api-types/dist/types";
import { ArticlePreviewResponse } from "authory-api-types/dist/types";
import { EmbedMode, EmbedTheme } from "../../utils/embeds";
import { getDisplayName } from "../../utils/getDisplayName";
import { V3ProfileAvatar } from "../ProfileAvatar";
import { V3ProfileHeroColorVariants } from "../ProfileHero/V3ProfileHero.types";
import { V3ProfileContact, V3ProfileSocials } from "../ProfileHero/V3ProfileSocials";
import { V3EmbedCollectionSeparator, V3EmbedCollectionTitle, V3EmbedCTA } from "../V3EmbedCollection";
import { V3EmbedContentListItem } from "../V3EmbedCollection/V3EmbedContentListitem";
import { EmbedWrapper } from "../V3EmbedContent";
import { VerticalSpacing } from "../VerticalSpacing";
import {
    V3EmbedProfileAvatarWrapper,
    V3EmbedProfileBio,
    V3EmbedProfileContent,
    V3EmbedProfileFooter,
    V3EmbedProfileItemsImageWrapper,
    V3EmbedProfileNoItemsImageWrapper
} from "./V3EmbedProfile.styles";
import { UnstyledLink } from "../ReusableStyledComponents/UnstyledLink";
import { ProfileSocialsContainer } from "../ProfileHero/V4ProfileHero.styles";
import { decodeEntities } from "../../utils/decodeHTMLEntities";

interface V3EmbedProfileProps {
    embedTheme?: EmbedTheme;
    embedMode?: EmbedMode;
    author: UserResponse;
    items: ArticlePreviewResponse[];
    showSocialMedia?: boolean;
    showHighlight?: boolean;
    useCover?: boolean;
    numBioLines?: number;
}

export const V3EmbedProfile = ({
    embedTheme = EmbedTheme.light,
    embedMode = EmbedMode.normal,
    author,
    items,
    showSocialMedia = true,
    showHighlight = true,
    useCover = true,
    numBioLines = 1
}: V3EmbedProfileProps) => {
    const src = !!author.avatarUrl.length ? author.avatarUrl[0].url : undefined;
    const target = `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${author.slug}`;

    const hasItems = !!items.length;
    const hasBackgroundImage = author.coverUrl.length > 1 && author.coverStyle !== UserCoverStyle.none && useCover;

    const useCustomItemsWrapper = hasBackgroundImage && hasItems;
    const ItemsWrapperToUse = useCustomItemsWrapper ? V3EmbedProfileItemsImageWrapper : React.Fragment;
    const ItemsWrapperToUseProps = useCustomItemsWrapper ? { $embedTheme: embedTheme } : {};
    const FooterToUse = hasBackgroundImage && !hasItems ? V3EmbedProfileFooter : React.Fragment;

    const useSeparator = (hasItems && !hasBackgroundImage) || !hasBackgroundImage;

    const Content = () => (
        <>
            <V3EmbedProfileContent $embedMode={embedMode} $imgSrc={hasItems && hasBackgroundImage ? author.coverUrl[1].url : undefined}>
                {!!src && (
                    <VerticalSpacing bottom={embedMode === EmbedMode.normal ? 8 : 0} style={{ display: "inline-flex" }}>
                        <UnstyledLink href={target} target="_blank">
                            <V3EmbedProfileAvatarWrapper>
                                <V3ProfileAvatar
                                    size={embedMode === EmbedMode.normal ? "EMBED_NORMAL" : "EMBED_COMPACT"}
                                    src={src}
                                    authorName={getDisplayName(author)}
                                />
                            </V3EmbedProfileAvatarWrapper>
                        </UnstyledLink>
                    </VerticalSpacing>
                )}
                <div>
                    <VerticalSpacing bottom={10}>
                        <UnstyledLink href={target} target="_blank">
                            <V3EmbedCollectionTitle as="h2" $embedTheme={hasBackgroundImage ? EmbedTheme.dark : embedTheme} $numLines={1}>
                                {getDisplayName(author)}
                            </V3EmbedCollectionTitle>
                        </UnstyledLink>
                    </VerticalSpacing>
                    {!!author.bio.length && (
                        <VerticalSpacing bottom={10}>
                            <UnstyledLink href={target} target="_blank">
                                <V3EmbedProfileBio $embedTheme={hasBackgroundImage ? EmbedTheme.dark : embedTheme} $numLines={numBioLines}>
                                    {decodeEntities(author.bio.split("\n")[0])}
                                </V3EmbedProfileBio>
                            </UnstyledLink>
                        </VerticalSpacing>
                    )}
                    <ProfileSocialsContainer $isEmbed $EmbedMode={embedMode}>
                        <div>
                            <V3ProfileSocials
                                colorVariant={
                                    embedTheme === EmbedTheme.light && !hasBackgroundImage
                                        ? V3ProfileHeroColorVariants.dark
                                        : V3ProfileHeroColorVariants.light
                                }
                                author={author}
                                noOpacity={false}
                                customJustify={embedMode === EmbedMode.compact ? "flex-start" : undefined}
                                showSocialMedia={showSocialMedia}
                                profileIconLimit={4}
                            />
                        </div>
                        <div>
                            <V3ProfileContact
                                openContactModal={() => {
                                    window.open(`${target}?target=contact`, "_blank");
                                }}
                                showHighlight={showHighlight}
                                colorVariant={
                                    embedTheme === EmbedTheme.light && !hasBackgroundImage
                                        ? V3ProfileHeroColorVariants.dark
                                        : V3ProfileHeroColorVariants.light
                                }
                                author={author}
                                noOpacity={false}
                                isEmbed
                            />
                        </div>
                    </ProfileSocialsContainer>
                </div>
            </V3EmbedProfileContent>
            {useSeparator && (
                <VerticalSpacing bottom={20} top={16}>
                    <V3EmbedCollectionSeparator />
                </VerticalSpacing>
            )}
            <ItemsWrapperToUse {...ItemsWrapperToUseProps}>
                {items.map((item, index) => (
                    <VerticalSpacing bottom={index === items.length - 1 ? 20 : 16} key={index}>
                        <V3EmbedContentListItem item={item} embedTheme={embedTheme} itemsHref={`/${author.slug}`} />
                    </VerticalSpacing>
                ))}
                <FooterToUse>
                    <V3EmbedCTA target={target} label="View portfolio" embedTheme={embedTheme} utmCampaign="portfolio" />
                </FooterToUse>
            </ItemsWrapperToUse>
        </>
    );

    if (hasBackgroundImage) {
        return (
            <V3EmbedProfileNoItemsImageWrapper $imgSrc={!hasItems ? author.coverUrl[1].url : undefined}>
                <Content />
            </V3EmbedProfileNoItemsImageWrapper>
        );
    }

    return (
        <EmbedWrapper embedTheme={embedTheme}>
            <Content />
        </EmbedWrapper>
    );
};
